import { useActionSheet } from '@expo/react-native-action-sheet';
import { addHours, format, isAfter, isBefore, subHours } from 'date-fns';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import {
  Dimensions,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import type { ICarouselInstance } from 'react-native-reanimated-carousel';
import Carousel, { Pagination } from 'react-native-reanimated-carousel';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useReservation } from '@/api/reservations/use-reservation';
import { useUploadTripPhotos } from '@/api/reservations/use-upload-trip-photos';
import BackButton from '@/components/back-button';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import {
  compressAndResize,
  isDesktop,
  runWithConcurrencyLimit,
  uniqByFilename,
} from '@/core';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, showErrorMessage, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Icon } from '@/ui/icon';
import { Replace } from '@/ui/icons/replace';
import { TrashFill } from '@/ui/icons/trash-fill';
import { Loader } from '@/ui/loader';

const PAGE_WIDTH = Dimensions.get('window').width;

const TUTORIAL_STEPS = [
  {
    source: require('../../../../../public/images/upload-car-1.png'),
    type: 'Exterior',
    title: 'Capture the sides of the car.',
    body: 'Ensure the entire car is visible.',
  },
  {
    source: require('../../../../../public/images/upload-car-2.png'),
    type: 'Exterior',
    title: 'Capture the back and front of the car',
    body: 'Ensure the entire car is visible.',
  },
  {
    source: require('../../../../../public/images/upload-car-3.png'),
    type: 'Exterior',
    title: 'Capture the rims',
    body: 'Ensure all the rim is visible.',
  },
  {
    source: require('../../../../../public/images/upload-car-4.png'),
    type: 'Interior',
    title: 'Capture the dashboard and seats.',
    body: 'Ensure all seats, the dashboard, and the steering wheel are visible.',
  },
];

export default function UploadPhotos() {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams();
  const { showActionSheetWithOptions } = useActionSheet();

  const router = useRouter();
  const progress = useSharedValue<number>(0);
  const [carouselWidth, setCarouselWidth] = React.useState(0);
  const carouselRef = React.useRef<ICarouselInstance>(null);
  const [carouselActiveIndex, setCarouselActiveIndex] = React.useState(0);
  const [showLeaveModal, setShowLeaveModal] = React.useState(false);
  const [uploadingPhotos, setUploadingPhotos] = React.useState(false);
  const uploadPhotosTutorialSeen = useSettingsStore(
    (state) => state.uploadPhotosTutorialSeen
  );
  const setUploadPhotosTutorialSeen = useSettingsStore(
    (state) => state.setUploadPhotosTutorialSeen
  );
  const [images, setImages] = React.useState<ImagePicker.ImagePickerAsset[]>();

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    //@ts-ignore
    variables: { id: id },
    enabled: !!id,
  });

  const { mutateAsync: uploadTripPhotos, isPending: isUploadingTripPhotos } =
    useUploadTripPhotos();

  const isPostTrip = useMemo(() => {
    if (!resData) return false;

    return isAfter(new Date(), subHours(new Date(resData.endTime), 3));
  }, [resData]);

  React.useEffect(() => {
    carouselRef.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: carouselActiveIndex - progress.value,
      animated: false,
    });

    if (carouselActiveIndex === TUTORIAL_STEPS.length) {
      setUploadPhotosTutorialSeen(true);
    }
  }, [carouselActiveIndex]);

  const imgsToShow = useMemo(() => {
    if (!resData) return [];

    const isPreTrip = isBefore(
      new Date(),
      addHours(new Date(resData.startTime), 6)
    );

    return isPreTrip ? resData.preTripCarImages : resData.postTripCarImages;
  }, [resData]);

  React.useEffect(() => {
    if (imgsToShow?.length > 0) {
      setUploadPhotosTutorialSeen(true);
    }
  }, [imgsToShow, setUploadPhotosTutorialSeen]);

  React.useEffect(() => {
    const convertToFiles = async () => {
      const convertedImages = await Promise.all(
        imgsToShow.map(async (carImage) => {
          const response = await fetch(carImage.photo);
          const blob = await response.blob();
          const fileName = carImage.photo.split('/').pop();

          const file = new File([blob], fileName!, {
            type: blob.type,
          });

          return {
            file,
            name: carImage.photo.split('/').pop(),
            uri: carImage.photo, // Add the uri property for native platforms
          };
        })
      );

      setImages(convertedImages);
    };

    if (resData?.carImages) {
      convertToFiles();
    }
  }, [resData?.carImages]);

  const onPressPagination = (index: number) => {
    setCarouselActiveIndex(index);
  };

  const handleNext = () => {
    setCarouselActiveIndex(carouselActiveIndex + 1);
  };

  const handleUploadPhotos = async (replaceImageFileName = null) => {
    let imgs = replaceImageFileName
      ? images?.filter(
          (img) => (img.fileName || img.name) !== replaceImageFileName
        )
      : images;

    if (Platform.OS === 'web') {
      try {
        const result = await DocumentPicker.getDocumentAsync({
          type: ['image/jpeg', 'image/png', 'image/heic'],
          copyToCacheDirectory: false,
          multiple: true,
        });

        if (!result.canceled) {
          const assets = await Promise.all(
            result.assets.map(async (asset) => {
              const response = await fetch(asset.uri);
              const blob = await response.blob();

              const file = new File([blob], asset.name || 'file.jpg', {
                type: asset.mimeType || 'image/jpeg',
              });

              return {
                file,
                name: asset.name,
                uri: asset.uri,
              };
            })
          );

          setImages(uniqByFilename([...(imgs || []), ...assets]));
        }
      } catch (error) {
        console.error('Error picking file:', error);
      }

      return;
    }

    const options = [
      'Choose from Photos',
      // 'Take a Photo',
      'Upload Files',
      'Cancel',
    ];
    const selectPhotoIdx = 0;
    // const takeAPhotoIdx = 1;
    const uploadFilesIdx = 1;
    const cancelButtonIndex = 2;

    showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex,
        destructiveButtonIndex: cancelButtonIndex,
      },
      async (selectedIndex: number) => {
        switch (selectedIndex) {
          // case takeAPhotoIdx: {
          //   const permission = await ImagePicker.getCameraPermissionsAsync();
          //   if (!permission.granted) {
          //     const permissionResult =
          //       await ImagePicker.requestCameraPermissionsAsync();

          //     if (!permissionResult.granted) {
          //       return;
          //     }
          //   }

          //   const result = await ImagePicker.launchCameraAsync({
          //     base64: true,
          //   });

          //   if (!result.canceled) {
          //     setImages(uniqByFilename([...imgs, ...result.assets]));
          //   }

          //   break;
          // }
          case uploadFilesIdx: {
            try {
              setUploadingPhotos(true);

              const result = await DocumentPicker.getDocumentAsync({
                type: ['image/jpeg', 'image/png', 'image/heic'],
                copyToCacheDirectory: false,
                multiple: true,
              });

              if (!result.canceled) {
                const compressedResults = await runWithConcurrencyLimit(
                  result.assets.map((asset) => () => compressAndResize(asset)),
                  3 // concurrency limit
                );

                const formattedAssets = await Promise.all(
                  compressedResults.map(async (res, index) => {
                    const file: string = await FileSystem.readAsStringAsync(
                      res.uri,
                      {
                        encoding: FileSystem.EncodingType.Base64,
                      }
                    );

                    return {
                      ...result.assets[index],
                      fileName: result.assets[index].name,
                      base64: file,
                      uri: res.uri,
                    };
                  })
                );

                setImages(
                  uniqByFilename([...(imgs || []), ...formattedAssets])
                );
              }
            } catch (error) {
              console.error('Error picking file:', error);
            } finally {
              setUploadingPhotos(false);
            }
            break;
          }
          case selectPhotoIdx: {
            setUploadingPhotos(true);

            const result = await ImagePicker.launchImageLibraryAsync({
              mediaTypes: ImagePicker.MediaTypeOptions.Images,
              allowsEditing: false,
              allowsMultipleSelection: true,
              base64: true,
              selectionLimit: 15,
            });

            if (!result.canceled) {
              const compressedResults = await runWithConcurrencyLimit(
                result.assets.map((asset) => () => compressAndResize(asset)),
                3 // concurrency limit
              );

              const formattedResults = compressedResults.map((res, index) => ({
                ...result.assets[index],
                uri: res.uri,
                base64: res.base64,
              }));

              setImages(uniqByFilename([...(imgs || []), ...formattedResults]));
            }

            setUploadingPhotos(false);
            break;
          }

          case cancelButtonIndex:
          // Canceled
        }
      }
    );
  };

  const handleSavePhotos = async () => {
    try {
      const formattedImages =
        Platform.OS === 'web' ? images?.map((img) => img.file) : images;

      if (formattedImages!.length > 15) {
        showErrorMessage('You can only upload a maximum of 15 photos.');
        return;
      }

      await uploadTripPhotos({
        id,
        images: formattedImages,
      });

      Toast.show({
        type: 'success',
        text1: 'Photos uploaded',
      });

      if (router.canGoBack()) {
        router.back();
      } else {
        router.navigate(`/trips/${id}/details`);
      }
    } catch (e) {
      showErrorMessage(e.message);
    }
  };

  const renderBody = () => {
    if (uploadPhotosTutorialSeen) {
      return (
        <View style={{ flexGrow: 1, justifyContent: 'space-between' }}>
          <View style={{ flexBasis: 0, flexGrow: 1, padding: 16, gap: 16 }}>
            <View
              style={{
                alignItems: 'flex-end',
              }}
            >
              <Button
                text="Upload Photos"
                type="primarySmall"
                onPress={handleUploadPhotos}
                style={{ width: 150 }}
              />
            </View>

            {uploadingPhotos ? (
              <View
                style={{
                  flexGrow: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Loader style={{ width: 100, height: 100 }} />
              </View>
            ) : (
              <ScrollView
                contentContainerStyle={{ gap: 16, flexGrow: 1 }}
                style={{ height: '70%' }}
                showsVerticalScrollIndicator={false}
              >
                {images?.map((image, index) => {
                  const imageFileName = image.fileName || image.name;
                  let imageUrl = image.uri;

                  return (
                    <View
                      key={`image-${index}`}
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <View
                          style={{
                            backgroundColor: theme.colors.background_2,
                            width: 60,
                            height: 60,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Image
                            source={{ uri: imageUrl }}
                            style={{ width: 60, height: 60 }}
                            contentFit="contain"
                          />
                        </View>

                        <View>
                          <Text style={{ width: 170 }}>{imageFileName}</Text>
                          <Text>{format(new Date(), 'MMM dd, yyyy')}</Text>
                        </View>
                      </View>

                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <Pressable
                          onPress={async () => {
                            handleUploadPhotos(imageFileName);
                          }}
                          style={({ pressed }) => [
                            {
                              opacity: pressed ? 0.5 : 1,
                              backgroundColor: 'transparent',
                            },
                          ]}
                        >
                          <Icon
                            type="dressed"
                            icon={<Replace />}
                            fillColor={theme.colors.icon.color}
                            width={20}
                            height={20}
                          />
                        </Pressable>

                        <Pressable
                          onPress={() => {
                            setImages(images.filter((_, i) => i !== index));
                          }}
                          style={({ pressed }) => [
                            {
                              opacity: pressed ? 0.5 : 1,
                              backgroundColor: 'transparent',
                            },
                          ]}
                        >
                          <Icon
                            type="dressed"
                            icon={<TrashFill />}
                            fillColor={theme.colors.icon.color}
                            width={20}
                            height={20}
                          />
                        </Pressable>
                      </View>
                    </View>
                  );
                })}

                {images?.length === 0 ? (
                  <View
                    style={{
                      flexGrow: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <View style={{ width: '80%' }}>
                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                          textAlign: 'center',
                        }}
                      >
                        No photos yet.{`\n`} Upload photos to get started.
                      </Text>
                    </View>
                  </View>
                ) : null}
              </ScrollView>
            )}
          </View>

          <View style={styles.bottomContainer}>
            <View style={{ padding: 16, gap: 16 }}>
              <Text
                type="caption1"
                style={{
                  color: theme.colors.text.secondary,
                  textAlign: 'center',
                }}
              >
                {isPostTrip
                  ? 'You can only save post-trip photos. Max 15 photos allowed.'
                  : 'Save your pre-trip photos before starting your trip. Max 15 photos allowed.'}
              </Text>

              <Button
                text="Save"
                type="primary"
                onPress={handleSavePhotos}
                // disabled={imgsToShow.length > images?.length}
                disabled={images?.length === 0 && imgsToShow.length === 0}
              />
            </View>
          </View>
        </View>
      );
    }

    return (
      <View style={{ flexGrow: 1, justifyContent: 'space-between' }}>
        <View>
          <View style={{ height: 500, paddingTop: 40 }}>
            {carouselWidth !== 0 ? (
              <Carousel
                ref={carouselRef}
                width={carouselWidth}
                style={{
                  width: '100%',
                }}
                height={500}
                data={TUTORIAL_STEPS}
                loop={false}
                onProgressChange={progress}
                onSnapToItem={(index) => {
                  setCarouselActiveIndex(index);
                }}
                renderItem={({ item }) => {
                  return (
                    <View
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 24,
                        padding: 16,
                      }}
                    >
                      <Image
                        source={item.source}
                        style={{ width: '100%', height: 320 }}
                        contentFit="contain"
                      />

                      <View style={{ gap: 4 }}>
                        <Text
                          type="caption1"
                          style={{
                            color: theme.colors.text.secondary,
                            textAlign: 'center',
                          }}
                        >
                          {item.type}
                        </Text>

                        <Text type="title2" style={{ textAlign: 'center' }}>
                          {item.title}
                        </Text>

                        <Text
                          type="bodyBold"
                          style={{
                            color: theme.colors.text.secondary,
                            textAlign: 'center',
                          }}
                        >
                          {item.body}
                        </Text>
                      </View>
                    </View>
                  );
                }}
              />
            ) : null}
          </View>

          <Pagination.Basic
            progress={progress}
            data={TUTORIAL_STEPS}
            dotStyle={{
              backgroundColor: theme.colors.paginationDots.empty,
              borderRadius: 50,
            }}
            activeDotStyle={{
              backgroundColor: theme.colors.paginationDots.filled,
            }}
            size={8}
            containerStyle={{ gap: 10, marginTop: 10 }}
            onPress={onPressPagination}
          />
        </View>

        <View style={{ padding: 16, paddingBottom: 24 }}>
          <Button text="Next" type="primary" onPress={handleNext} />
        </View>
      </View>
    );
  };

  if (
    isFetchingReservation ||
    (resData?.carImages?.length > 0 && images?.length === undefined) ||
    isUploadingTripPhotos
  ) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 200, height: 200 }} />
      </View>
    );
  }

  return (
    <View style={styles.parentContainer}>
      <View
        style={styles.container}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;

          ['md', 'lg'].includes(UnistylesRuntime.breakpoint)
            ? setCarouselWidth(width)
            : setCarouselWidth(PAGE_WIDTH);
        }}
      >
        {!isDesktop() ? (
          <Stack.Screen
            options={{
              title: isPostTrip ? 'Post-Trip Photos' : 'Pre-Trip Photos',
              headerTitle: isPostTrip ? 'Post-Trip Photos' : 'Pre-Trip Photos',
              headerLeft: () => (
                <BackButton
                  onPress={() => {
                    if (uploadPhotosTutorialSeen && images.length === 0) {
                      setShowLeaveModal(true);
                      return;
                    }

                    router.navigate(`/trips/${id}/details`);
                  }}
                />
              ),
            }}
          />
        ) : null}

        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (router.canGoBack()) {
                  router.back();
                } else {
                  router.navigate(`/trips/${id}/details`);
                }
              }}
            />

            <Text type="largeTitle">Upload Trip Photos</Text>
          </View>
        ) : null}

        {renderBody()}

        <ConfirmModal
          isVisible={showLeaveModal}
          titleTx="uploadPhotos.leaveModalTitle"
          body={
            <View>
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Take clear photos before and after your trip to prove the car’s
                condition, ensuring you’re not wrongly blamed for pre-existing
                or post-drop-off damage.
              </Text>
            </View>
          }
          leftButton={{
            tx: 'uploadPhotos.leaveModalCancelButton',
            onPress: () => {
              setShowLeaveModal(false);
            },
          }}
          rightButton={{
            tx: 'uploadPhotos.leaveModalLeaveButton',
            onPress: () => {
              setShowLeaveModal(false);
              router.navigate(`/trips/${id}/details`);
            },
          }}
          onDismiss={() => {
            setShowLeaveModal(false);
          }}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  parentContainer: {
    backgroundColor: theme.colors.background_1,
    flex: 1,
    alignItems: {
      md: 'center',
    },
  },
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    justifyContent: 'space-between',
    width: {
      md: 600,
    },
    height: {
      md: 600,
    },
  },
  bottomContainer: {
    backgroundColor: {
      xs: theme.colors.background_2,
      md: 'inherit',
    },
    height: 150,
  },
}));
